import React, { useContext, useEffect, useState } from 'react';

import { AccountContext } from './AccountContext';

import { Filter, FilterResult, StandardFilterValues } from '../Filter/Types';
import { AccountOnlineAnalysis, AccountOrProfile } from '../MyLom/Account/EditAccount/Types';

import { createAccountOrProfile } from '../Api/Account/Utils';

import { getFilteredProducts } from '../Api/Filter';

declare global {
    interface Window {
        __GGCareStepNodeId: string | undefined;
        __GGCareStepName: string;
        __GGCareSectionId: string | undefined;
    }
}

interface FilterContextInterface {
    standardFilterValues: StandardFilterValues | null;
    updateStandardFilterValues: (standardFilterValues: StandardFilterValues | null) => void;
    activeSkinAnalysis: string | null;
    updateActiveSkinanalysis: (activeSkinAnalysis: string | null) => void;
    result: FilterResult[];
    isLoading: boolean;

    // TODO: Check if this is the CATEGORY or the CARESTEP, i think it's the latter!
    updateActiveCategoryNodeId: (activeCategoryNodeId: string) => void;
    accountOrProfile: AccountOrProfile | undefined;
    isOnlineAnalysisOverlayVisible: boolean;
    makeOnlineAnalysisOverlayVisible: React.Dispatch<React.SetStateAction<boolean>>;
    onlineAnalysis: AccountOnlineAnalysis | null;
    updateOnlineAnalysis: (onlineAnalysis: AccountOnlineAnalysis | null) => void;
    isFilterActive: boolean;
    activeSkinAnalysisOrOnlineAnalysisId: string | null;
    resetAllFilters: () => void;
}

export const standardFilterStorageKey = 'filter.standardFilterValues';
export const activeSkinAnalysisStorageKey = 'filter.activeSkinAnalysis';
export const onlineAnalysisStorageKey = 'filter.onlineAnalysis';

export const readInitialStandardFilterValuesFromLocalStorage = (): StandardFilterValues | null => {
    const savedStandardFilterValues = sessionStorage.getItem(standardFilterStorageKey);
    if (typeof savedStandardFilterValues === 'string' && JSON.parse(savedStandardFilterValues).hasOwnProperty('age')) {
        return JSON.parse(savedStandardFilterValues);
    }

    return null;
};

export const readInitialActiveSkinanalysisFromLocalStorage = (): string | null => {
    const activeSkinAnalysis = sessionStorage.getItem(activeSkinAnalysisStorageKey);
    if (typeof activeSkinAnalysis === 'string') {
        return JSON.parse(activeSkinAnalysis);
    }

    return null;
};

export const readInitialOnlineSkinanalysisFromLocalStorage = (): AccountOnlineAnalysis | null => {
    const onlineAnalysis = sessionStorage.getItem(onlineAnalysisStorageKey);
    if (
        typeof onlineAnalysis === 'string' &&
        JSON.parse(onlineAnalysis).hasOwnProperty('answers') &&
        JSON.parse(onlineAnalysis).hasOwnProperty('id')
    ) {
        return JSON.parse(onlineAnalysis);
    }

    return null;
};

const saveStandardFilterValuesToLocalStorage = (standardFilterValues: StandardFilterValues | null): void => {
    if (standardFilterValues === null) {
        sessionStorage.removeItem(standardFilterStorageKey);
        return;
    }

    sessionStorage.setItem(standardFilterStorageKey, JSON.stringify(standardFilterValues));
};

const saveActiveSkinanalysisToLocalStorage = (activeSkinAnalysis: string | null): void => {
    if (activeSkinAnalysis === null) {
        sessionStorage.removeItem(activeSkinAnalysisStorageKey);
        return;
    }

    sessionStorage.setItem(activeSkinAnalysisStorageKey, JSON.stringify(activeSkinAnalysis));
};

const saveonlineAnalysisToLocalStorage = (onlineAnalysis: AccountOnlineAnalysis | null): void => {
    if (onlineAnalysis === null) {
        sessionStorage.removeItem(onlineAnalysisStorageKey);
        return;
    }

    sessionStorage.setItem(onlineAnalysisStorageKey, JSON.stringify(onlineAnalysis));
};

const resetFiltersInLocalStorage = () => {
    sessionStorage.removeItem(onlineAnalysisStorageKey);
    sessionStorage.removeItem(activeSkinAnalysisStorageKey);
    sessionStorage.removeItem(standardFilterStorageKey);
};

export const makeFilter = (
    skinAnalysisId: string | null,
    standardFilterValues: StandardFilterValues | null,
    onlineAnalysis: AccountOnlineAnalysis | null,
): Filter | null => {
    if (skinAnalysisId) {
        return {
            type: 'skinAnalysis',
            skinAnalysisId,
        };
    }
    if (standardFilterValues) {
        return {
            type: 'standardFilter',
            values: standardFilterValues,
        };
    }
    if (onlineAnalysis) {
        return {
            type: 'onlineAnalysis',
            values: onlineAnalysis.answers,
        };
    }
    return null;
};

export const FilterContext = React.createContext<FilterContextInterface>({
    standardFilterValues: readInitialStandardFilterValuesFromLocalStorage(),
    updateStandardFilterValues: () => {},
    activeSkinAnalysis: readInitialActiveSkinanalysisFromLocalStorage(),
    updateActiveSkinanalysis: () => {},
    result: [],
    isLoading: true,
    updateActiveCategoryNodeId: () => {},
    accountOrProfile: undefined,
    isOnlineAnalysisOverlayVisible: false,
    makeOnlineAnalysisOverlayVisible: () => {},
    onlineAnalysis: readInitialOnlineSkinanalysisFromLocalStorage(),
    updateOnlineAnalysis: () => {},
    isFilterActive: false,
    activeSkinAnalysisOrOnlineAnalysisId: null,
    resetAllFilters: () => {},
});

export const FilterValueProvider: React.FunctionComponent = (props) => {
    const [standardFilterValues, updateStandardFilterValuesState] = React.useState<StandardFilterValues | null>(
        readInitialStandardFilterValuesFromLocalStorage(),
    );
    const [activeSkinAnalysis, updateActiveSkinAnalysisState] = React.useState<string | null>(
        readInitialActiveSkinanalysisFromLocalStorage(),
    );
    const [onlineAnalysis, updateOnlineAnalysisState] = React.useState<AccountOnlineAnalysis | null>(
        readInitialOnlineSkinanalysisFromLocalStorage(),
    );
    const [result, setResult] = React.useState<FilterResult[]>([]);
    const [isLoading, setLoading] = React.useState(true);
    const [activeCategoryNodeId, setActiveCategoryNodeId] = React.useState<string>(window.__GGCareStepNodeId as string);
    const { accountData, isLoggedIn } = useContext(AccountContext);

    const [isOnlineAnalysisOverlayVisible, makeOnlineAnalysisOverlayVisible] = useState(false);

    const fetchResults = (filter: Filter | null, categoryNodeId: string) => {
        if (!categoryNodeId) {
            setLoading(false);
            return null;
        }
        setLoading(true);

        getFilteredProducts(
            categoryNodeId,
            filter,
            (filteredProducts) => {
                setResult(filteredProducts);
                setLoading(false);
            },
            () => {},
        );
    };

    useEffect(() => {
        fetchResults(makeFilter(activeSkinAnalysis, standardFilterValues, onlineAnalysis), activeCategoryNodeId);
    }, [standardFilterValues, activeSkinAnalysis, onlineAnalysis]);

    const updateStandardFilterValues = (updatedStandardFilterValues: StandardFilterValues | null) => {
        updateStandardFilterValuesState(updatedStandardFilterValues);
        saveStandardFilterValuesToLocalStorage(updatedStandardFilterValues);
        if (updatedStandardFilterValues) {
            // make sure there is always only one type of filter active
            updateActiveSkinanalysis(null);
            updateOnlineAnalysis(null);
        }
    };

    const updateActiveSkinanalysis = (skinAnalysisId: string | null) => {
        updateActiveSkinAnalysisState(skinAnalysisId);
        saveActiveSkinanalysisToLocalStorage(skinAnalysisId);

        if (skinAnalysisId) {
            // make sure there is always only one type of filter active
            updateStandardFilterValues(null);
            updateOnlineAnalysis(null);
        }
    };

    const updateOnlineAnalysis = (updatedOnlineAnalysis: AccountOnlineAnalysis | null) => {
        updateOnlineAnalysisState(updatedOnlineAnalysis);
        saveonlineAnalysisToLocalStorage(updatedOnlineAnalysis);

        if (updatedOnlineAnalysis) {
            // make sure there is always only one type of filter active
            updateActiveSkinanalysis(null);
            updateStandardFilterValues(null);
        }
    };

    const updateActiveCategoryNodeId = (categoryNodeId: string) => {
        setActiveCategoryNodeId(categoryNodeId);
    };

    const accountOrProfileFromSkinAnalysis = (): AccountOrProfile | undefined => {
        if (!accountData) {
            return undefined;
        }

        const profileThatHoldsSkinAnalysis = accountData.profiles.find(
            (profile) =>
                profile.skinAnalyses &&
                profile.skinAnalyses.some((skinAnalysis) => skinAnalysis.id === activeSkinAnalysis),
        );

        return profileThatHoldsSkinAnalysis
            ? createAccountOrProfile(profileThatHoldsSkinAnalysis)
            : createAccountOrProfile(accountData);
    };

    const accountOrProfile = accountOrProfileFromSkinAnalysis();

    const resetAllFilters = () => {
        updateStandardFilterValues(null);
        updateActiveSkinanalysis(null);
        updateOnlineAnalysis(null);
        resetFiltersInLocalStorage();
    };

    return (
        <FilterContext.Provider
            value={{
                standardFilterValues,
                updateStandardFilterValues,
                activeSkinAnalysis,
                updateActiveSkinanalysis,
                result,
                isLoading,
                updateActiveCategoryNodeId,
                accountOrProfile,
                isOnlineAnalysisOverlayVisible,
                makeOnlineAnalysisOverlayVisible,
                onlineAnalysis,
                updateOnlineAnalysis,
                isFilterActive:
                    (isLoggedIn === true && activeSkinAnalysis !== null) ||
                    (isLoggedIn === true && onlineAnalysis !== null) ||
                    standardFilterValues !== null,
                activeSkinAnalysisOrOnlineAnalysisId: activeSkinAnalysis
                    ? activeSkinAnalysis
                    : onlineAnalysis !== null
                      ? onlineAnalysis.id
                      : null,
                resetAllFilters,
            }}
        >
            {props.children}
        </FilterContext.Provider>
    );
};
